import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { ClerkAuthProvider, useAuth, RequireAuth, SignInPage, SignUpPage } from './contexts/ClerkAuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { apiClient } from './services/api';
import TopBar from './components/navigation/TopBar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DesktopShell from './layouts/DesktopShell';
import Video from './pages/Video';
import Image from './pages/Image';
import MyVideos from './pages/MyVideos';
import PricingPage from './components/PricingPage';
import HomePage from './pages/HomePage';
import ImageToVideoPage from './pages/ImageToVideoPage';
import ImageEditingPage from './pages/ImageEditingPage';
import TemplateToVideoPage from './pages/TemplateToVideoPage';
import TemplatesPage from './pages/TemplatesPage';
import UpgradePage from './pages/UpgradePage';
import CheckoutEmbeddedPage from './pages/CheckoutEmbeddedPage';
import CheckoutReturnPage from './pages/CheckoutReturnPage';
import CreditsPage from './pages/CreditsPage';
import CreditsSuccessPage from './pages/CreditsSuccessPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ClerkAuthModal from './components/auth/ClerkAuthModal';
import RequireAuthGate from './components/guards/RequireAuthGate';
import ErrorBoundary from './components/ErrorBoundary';
import { updateLastNonLoginPath } from './utils/authNavigation';
import { useTracking } from './hooks/useTracking';
import { getSubscriptionPlans } from './services/credits';
import { getUserAssets } from './services/assets';
import { usePostHog } from 'posthog-js/react';
import { getOrCreateUser } from './services/userService';

// Lazy load Lander1Page for code splitting - high-traffic landing page
const Lander1Page = lazy(() => import('./pages/Lander1Page'));
const Lander1Skeleton = lazy(() => import('./components/skeletons/Lander1Skeleton'));

function AppContent() {
  const { loading: isAuthLoading, getAccessToken, isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useTracking();
  const posthog = usePostHog();

  // Sync Clerk user to Supabase on authentication
  useEffect(() => {
    if (isAuthenticated && user) {
      const syncUser = async () => {
        try {
          const token = await getAccessToken();
          await getOrCreateUser(
            user.id,
            user.email,
            user.user_metadata?.full_name,
            token
          );
        } catch (error) {
          console.error('Failed to sync user to Supabase:', error);
        }
      };
      syncUser();
    }
  }, [isAuthenticated, user, getAccessToken]);

  // Set up API client to use Clerk tokens
  useEffect(() => {
    apiClient.setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Prefetch commonly accessed data on app load to warm cache
  useEffect(() => {
    // Prefetch subscription plans (used on /upgrade page)
    getSubscriptionPlans().catch(() => {
      // Silent fail - cache warming is best-effort
    });

    // If user is authenticated, prefetch their videos
    if (isAuthenticated) {
      getUserAssets('video').catch(() => {
        // Silent fail - cache warming is best-effort
      });
    }
  }, [isAuthenticated]);

  // Force dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // Track last non-login path in sessionStorage
  useEffect(() => {
    updateLastNonLoginPath(location.pathname);
  }, [location.pathname]);

  // Track pageviews
  const trackRef = useRef(track);
  trackRef.current = track;
  
  useEffect(() => {
    trackRef.current('pageview', {
      page_path: location.pathname,
      page_title: document.title,
      page_url: window.location.href,
    });
  }, [location.pathname]);

  if (isAuthLoading) {
    return (
      <div className="h-screen bg-bg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Check if we're on the lander-1 page (hide all navigation)
  const isLander1 = location.pathname === '/lander-1';

  // Special layout for lander-1: no navigation, full page
  if (isLander1) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        {/* Clerk auth modal handler */}
        <ClerkAuthModal />
        <Suspense fallback={<Lander1Skeleton />}>
          <Routes>
            <Route path="/lander-1" element={<Lander1Page />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  return (
    <div className="h-screen bg-bg dark:bg-bg flex flex-col overflow-x-hidden">
      <ScrollToTop />
      {/* Clerk auth modal handler */}
      <ClerkAuthModal />
      <TopBar />
      
      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col h-full">
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <Suspense fallback={
            <div className="min-h-screen bg-bg flex items-center justify-center">
              <div className="animate-pulse text-white/60">Loading...</div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/video/:mode" element={<Video />} />
              <Route path="/image/:mode" element={<Image />} />
              <Route path="/image-to-video" element={<ImageToVideoPage />} />
              <Route path="/image-editing" element={<ImageEditingPage />} />
              <Route path="/template-to-video" element={<TemplateToVideoPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/lander-1" element={<Lander1Page />} />
              <Route path="/upgrade" element={<ErrorBoundary><UpgradePage /></ErrorBoundary>} />
              <Route path="/my-collection" element={<RequireAuthGate><MyVideos /></RequireAuthGate>} />
              <Route path="/assets" element={<Navigate to="/my-collection" replace />} />
              <Route path="/billing" element={<PricingPage />} />
              <Route path="/credits" element={<RequireAuthGate><CreditsPage /></RequireAuthGate>} />
              <Route path="/credits/success" element={<RequireAuthGate><CreditsSuccessPage /></RequireAuthGate>} />
              <Route path="/checkout" element={<CheckoutEmbeddedPage />} />
              <Route path="/return" element={<CheckoutReturnPage />} />
              <Route path="/login" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
            </Routes>
          </Suspense>
          <Footer />
        </main>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex flex-col h-full">
        <DesktopShell>
          <Suspense fallback={
            <div className="min-h-screen bg-bg flex items-center justify-center">
              <div className="animate-pulse text-white/60">Loading...</div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/video/:mode" element={<Video />} />
              <Route path="/image/:mode" element={<Image />} />
              <Route path="/image-to-video" element={<ImageToVideoPage />} />
              <Route path="/image-editing" element={<ImageEditingPage />} />
              <Route path="/template-to-video" element={<TemplateToVideoPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/lander-1" element={<Lander1Page />} />
              <Route path="/upgrade" element={<ErrorBoundary><UpgradePage /></ErrorBoundary>} />
              <Route path="/my-collection" element={<RequireAuthGate><MyVideos /></RequireAuthGate>} />
              <Route path="/assets" element={<Navigate to="/my-collection" replace />} />
              <Route path="/billing" element={<PricingPage />} />
              <Route path="/credits" element={<RequireAuthGate><CreditsPage /></RequireAuthGate>} />
              <Route path="/credits/success" element={<RequireAuthGate><CreditsSuccessPage /></RequireAuthGate>} />
              <Route path="/checkout" element={<CheckoutEmbeddedPage />} />
              <Route path="/return" element={<CheckoutReturnPage />} />
              <Route path="/login" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
            </Routes>
          </Suspense>
        </DesktopShell>
      </div>
    </div>
  );
}

function App() {
  return (
    <ClerkAuthProvider>
      <AuthModalProvider>
        <AppContent />
      </AuthModalProvider>
    </ClerkAuthProvider>
  );
}

export default App;
